// デコ（庭に置く飾り）のカタログ。純データ＝描画にも DOM にも依存しない。
//
// 庭システムの中核は「どのデコがどの属性をどれだけ持つか」。属性合計が
// M3 の客誘引条件を決めるので、ここが設計の正本になる。各デコは見た目の
// 表現（形と色）を render 層に持たせ、ここには属性とコストだけを置く。

/** 庭デコが持つ属性の種類（GAME_DESIGN.md の 7 属性）。 */
export type AttributeKey =
  | 'light' // 光
  | 'scent' // 香り
  | 'water' // 水辺
  | 'calm' // 静寂
  | 'lively' // にぎやか
  | 'crystal' // 結晶
  | 'green'; // 緑

/** 属性キーの全列挙（合計計算・テストの網羅に使う単一正本）。 */
export const ATTRIBUTE_KEYS: readonly AttributeKey[] = [
  'light',
  'scent',
  'water',
  'calm',
  'lively',
  'crystal',
  'green',
] as const;

/** 属性キー → 日本語表示名。UI のラベルに使う。 */
export const ATTRIBUTE_LABELS: Record<AttributeKey, string> = {
  light: '光',
  scent: '香り',
  water: '水辺',
  calm: '静寂',
  lively: 'にぎやか',
  crystal: '結晶',
  green: '緑',
};

/** 属性ベクトル。未指定の属性は 0 とみなす（疎な表現）。 */
export type AttributeVector = Partial<Record<AttributeKey, number>>;

/** デコ 1 種の定義。 */
export interface DecoDef {
  /** 安定 ID（セーブに焼かれるので将来も変えない）。 */
  id: string;
  /** 表示名（日本語）。 */
  name: string;
  /** UI のアイコン用絵文字（ドット絵テクスチャを作らない簡易表現）。 */
  icon: string;
  /** 属性ベクトル（庭全体の合計に寄与する）。 */
  attributes: AttributeVector;
  /** 設置コスト（チップ）。撤去時は全額返金される。 */
  cost: number;
  /**
   * 客の「席」になるか（v0.7.0）。true のデコは来店した客が着席して飲む場所になり、
   * 着席セルを中心に近接アンビエンスを積分してチップに反映する（seat.ts）。
   * 省略時は false（席ではない）。
   */
  seat?: boolean;
  /**
   * パレットに出さない特殊デコか（v0.7.0）。入口（entrance）のように自動配置・撤去不可で、
   * プレイヤーが任意に置けないデコに付ける。省略時は false（パレットに出る通常デコ）。
   */
  hidden?: boolean;
  /**
   * アップグレード不可か（v0.7.0）。入口など、レベルの概念を持たないデコに付ける。
   * 省略時は false（アップグレード可能＝level 1..MAX_LEVEL を持てる）。
   */
  noUpgrade?: boolean;
}

/**
 * デコカタログ（10 種）。順序は UI のパレット表示順。
 *
 * 属性のバランスは GAME_DESIGN.md の例（光/結晶/水辺の複合条件等）を
 * 満たせるように散らしてある。M3 で客の出現条件を組むときの土台。
 */
export const DECO_CATALOG: readonly DecoDef[] = [
  {
    id: 'violet_bed',
    name: '宇宙すみれの花壇',
    icon: '🌸',
    attributes: { scent: 2, green: 1 },
    cost: 8,
  },
  {
    id: 'crystal_cluster',
    name: 'クリスタル群晶',
    icon: '💎',
    attributes: { crystal: 3, light: 1 },
    cost: 14,
  },
  {
    id: 'lantern',
    name: 'ランタン',
    icon: '🏮',
    attributes: { light: 2, calm: 1 },
    cost: 6,
  },
  {
    id: 'pond',
    name: '小さな池',
    icon: '💧',
    attributes: { water: 2, calm: 1 },
    cost: 12,
  },
  {
    id: 'bench',
    name: 'ベンチ',
    icon: '🪑',
    attributes: { calm: 2 },
    cost: 5,
    seat: true, // 客が座って飲む席（v0.7.0）
  },
  {
    id: 'cafe_table',
    name: 'テーブル席',
    icon: '🪑',
    // 席種を増やす（v0.7.0）。ベンチより静寂寄り＋ほんのり灯り（卓上ランプの趣）。
    attributes: { calm: 1, light: 1 },
    cost: 11,
    seat: true,
  },
  {
    id: 'stone_tile',
    name: '石畳タイル',
    icon: '⬜',
    attributes: {}, // 無属性・通路
    cost: 2,
  },
  {
    id: 'glow_mushroom',
    name: '光るキノコ',
    icon: '🍄',
    attributes: { light: 1, scent: 1 },
    cost: 7,
  },
  {
    id: 'wind_chime',
    name: '風鈴ポール',
    icon: '🎐',
    attributes: { lively: 2, scent: 1 },
    cost: 9,
  },
  {
    id: 'fountain',
    name: '噴水',
    icon: '⛲',
    attributes: { water: 2, lively: 2 },
    cost: 16,
  },
  {
    id: 'sapling',
    name: '若木',
    icon: '🌱',
    // 成長は M4。今は 1 段階（緑のみ）。
    attributes: { green: 2 },
    cost: 10,
  },
  // --- 季節の結晶（v0.6.0）---
  // 設置している間その季節に固定する特殊デコ。チップ高め。撤去で自然サイクルへ戻る。
  // 季節固定の効果は season.ts（SEASON_CRYSTAL_BY_DECO）で判定する。属性は季節の趣を
  // ひとつ持たせる（結晶系なので crystal を共通で少しと、季節ごとの色属性）。
  {
    id: 'crystal_spring',
    name: '春の結晶',
    icon: '🌸',
    attributes: { crystal: 1, scent: 2 },
    cost: 60,
  },
  {
    id: 'crystal_summer',
    name: '夏の結晶',
    icon: '🌿',
    attributes: { crystal: 1, green: 2 },
    cost: 60,
  },
  {
    id: 'crystal_autumn',
    name: '秋の結晶',
    icon: '🍁',
    attributes: { crystal: 1, calm: 2 },
    cost: 60,
  },
  {
    id: 'crystal_winter',
    name: '冬の結晶',
    icon: '❄️',
    attributes: { crystal: 2, light: 1 },
    cost: 60,
  },
  // --- 入口ゲート（v0.7.0）---
  // 客が出現し、退店時に去る「導線の起点」。島手前のエッジに自動配置され、撤去・
  // アップグレード不可・パレットに出さない（hidden）。無属性（庭属性には寄与しない）。
  {
    id: 'entrance',
    name: '入口ゲート',
    icon: '⛩️',
    attributes: {},
    cost: 0,
    hidden: true,
    noUpgrade: true,
  },
];

/**
 * 入口ゲートのデコ ID（v0.7.0）。新規ゲーム・セーブ移行で島手前のエッジに 1 つだけ
 * 自動配置する。客はここに出現し、退店時もここから去る（presentation 層の導線の起点）。
 */
export const ENTRANCE_DECO_ID = 'entrance';

/** デコが客の「席」か（v0.7.0）。 */
export function isSeatDeco(decoId: string): boolean {
  const d = getDeco(decoId);
  return !!d && d.seat === true;
}

/** デコがアップグレード可能か（v0.7.0）。入口など noUpgrade は false。未知 ID も false。 */
export function isUpgradableDeco(decoId: string): boolean {
  const d = getDeco(decoId);
  return !!d && d.noUpgrade !== true;
}

/** ID → デコ定義の索引。未知 ID は undefined。 */
const DECO_BY_ID: ReadonlyMap<string, DecoDef> = new Map(
  DECO_CATALOG.map((d) => [d.id, d]),
);

/** ID からデコ定義を引く。未知 ID なら undefined。 */
export function getDeco(id: string): DecoDef | undefined {
  return DECO_BY_ID.get(id);
}

// =========================================================================
// タイルのアップグレード（v0.7.0、インクリメンタル要素）
// =========================================================================
//
// 各 GardenCell は level（1..MAX_LEVEL）を持つ。アップグレードでそのデコの属性が
// 強くなり、コストは level に応じて上がる。すべて純計算（乱数なし）なのでオフライン
// 精算でも効き、決定的。撤去時は base + 各 level の upgrade コストを全額返金する。

/** タイルレベルの上限。level は 1..MAX_LEVEL。 */
export const MAX_LEVEL = 5;

/**
 * レベルによる属性倍率。level 1 で 1.0、以降 0.5/level で線形に増える
 *  （level=L → 1 + 0.5*(L-1)）。level 5 で 3.0 倍。gardenAttributes / seatAmbiance が
 * これを掛けて「良いデコを育てるほど効く」を作る。
 */
export function levelAttributeMultiplier(level: number): number {
  const L = clampLevel(level);
  return 1 + 0.5 * (L - 1);
}

/** level を 1..MAX_LEVEL にクランプする（壊れたセーブ・範囲外を丸める）。 */
export function clampLevel(level: number): number {
  if (!Number.isFinite(level)) return 1;
  const L = Math.floor(level);
  return L < 1 ? 1 : L > MAX_LEVEL ? MAX_LEVEL : L;
}

/**
 * 現在 level の属性ベクトルを返す（各属性値に levelAttributeMultiplier を掛ける）。
 * 切り捨て・丸めはしない（積分側で小数のまま扱う）。level 1 は素のベクトルと同値。
 */
export function leveledAttributes(base: AttributeVector, level: number): AttributeVector {
  const mult = levelAttributeMultiplier(level);
  if (mult === 1) return base;
  const out: AttributeVector = {};
  for (const key of ATTRIBUTE_KEYS) {
    const v = base[key];
    if (v != null && v !== 0) out[key] = v * mult;
  }
  return out;
}

/**
 * level L から L+1 へ上げるためのアップグレードコスト（チップ）。
 *  baseCost × L × 1.6 を floor。base が小さいデコは下限 4 を保証して「実質無料」を避ける。
 * level が MAX 以上のときは Infinity（これ以上上げられない）を返す。
 */
export function upgradeCost(decoId: string, currentLevel: number): number {
  const deco = getDeco(decoId);
  if (!deco || !isUpgradableDeco(decoId)) return Infinity;
  const L = clampLevel(currentLevel);
  if (L >= MAX_LEVEL) return Infinity;
  const raw = Math.floor(Math.max(deco.cost, 1) * L * 1.6);
  return Math.max(4, raw);
}

/**
 * デコ 1 個の「投入総額」（base コスト + level 1→現在までの各アップグレードコストの合計）。
 * 撤去時の全額返金（やり直し無料の方針）に使う。
 */
export function totalInvested(decoId: string, level: number): number {
  const deco = getDeco(decoId);
  if (!deco) return 0;
  let total = deco.cost;
  const L = clampLevel(level);
  for (let lv = 1; lv < L; lv++) total += upgradeCost(decoId, lv);
  return total;
}
